/**
 * Quotas centralisés DooDates - Nouveau modèle simplifié 3 niveaux
 *
 * ⚠️ SOURCE DE VÉRITÉ UNIQUE - Ne pas dupliquer ces valeurs ailleurs
 *
 * Système de quotas simplifié :
 * - Anonyme : 3 conversations IA à vie
 * - Gratuit : 3 conversations IA à vie, plafond 10 sondages actifs
 * - Solo : 100 conversations IA par période de 30 jours (usage loyal publié),
 *   exports CSV/PDF/JSON/Markdown
 * - Tous : 3 modifications IA par proposition (MAX_MODIFICATIONS_PER_POLL) et
 *   20 messages utilisateur par conversation (MAX_MESSAGES_PER_CONVERSATION,
 *   décision de Julien du 26/10/2026, appliquée côté client dans
 *   GeminiChatInterface). L'ancien AI_MESSAGE_QUOTAS 5/10 n'était appliqué
 *   nulle part — retiré le 29/09/2026 ; ce plafond de 20 le remplace.
 *
 * Principes :
 * - La création manuelle de sondages est TOUJOURS gratuite et illimitée (anonyme inclus)
 * - L'IA est le levier de conversion (goûter la magie → payer pour la garder)
 * - Anonyme et Free partagent UN SEUL quota de 3 conversations IA à vie
 *   (pas 3+3) : aucun bénéfice IA à créer un compte gratuit, le compte sert
 *   à conserver son travail (sondage, dashboard, exports). L'usage anonyme
 *   déjà consommé est reporté sur le compte à la création (RPC
 *   `merge_guest_ai_quota_to_user`, cf. migration
 *   20260910120000_merge_guest_ai_quota_on_signup.sql et AuthContext.tsx) ;
 *   Solo = seule offre qui relève ce plafond.
 * - Le Fair Use de Solo n'est plus invisible depuis le 15/09/2026 : 100
 *   conversations par période de 30 jours, publiées dans `ia-policy.md` §1,
 *   dans les CGV §1 et sur la carte Solo de `/pricing`. Le chiffre reste
 *   absent des CONSTANTES CLIENT ci-dessous, et c'est voulu — voir le
 *   commentaire de CONVERSATION_QUOTAS.SOLO.
 *
 * Dernière mise à jour : 15 Sept 2026 - Publication du Fair Use Solo (100
 * conversations / 30 jours) là où trois documents annonçaient un illimité ;
 * retrait d'Excel et de l'intégration calendrier de l'offre Solo.
 * Avant cela, 10 Sept 2026 - Doctrine clarifiée : le plafond de 3
 * est un total PARTAGÉ entre anonyme et Free (pas deux compteurs indépendants
 * de 3 chacun) ; se créer un compte gratuit ne remet jamais le compteur à
 * zéro. Voir aussi le 9 Sept 2026 (Free aligné à 3, numériquement).
 */

// ============================================================================
// QUOTAS CONVERSATIONS IA
// ============================================================================

/**
 * Nombre maximum de conversations IA par utilisateur
 *
 * Une conversation = une session de création de sondage avec l'IA
 * Les modifications d'un sondage existant ne comptent pas comme nouvelle conversation
 */
export const CONVERSATION_QUOTAS = {
  /** Utilisateurs anonymes : 3 conversations IA à vie (non renouvelable) */
  ANONYMOUS: 3,

  /** Utilisateurs gratuits : 3 conversations IA à vie (non renouvelable), aligné sur anonyme */
  FREE: 3,

  /**
   * Utilisateurs Solo : Fair Use de 100 conversations par période de 30 jours.
   *
   * La valeur 999999 n'est PAS un illimité : c'est un plafond client
   * volontairement inatteignable, parce que le vrai quota est tenu côté serveur
   * par `consume_ai_credit` (seed 100, remis à 100 quand
   * `last_reset_at < now() - 30 jours`). Le client n'a pas de quoi calculer
   * cette fenêtre glissante, il ne doit donc pas prétendre le faire.
   *
   * Depuis le 15/09/2026, ce Fair Use n'est plus invisible : il est publié dans
   * `ia-policy.md` §1, dans les CGV §1 (`cg-u.md`, fr/en/es) et sur la carte
   * Solo de `/pricing` (clé `pricing.feature.ai_fair_use`). Changer la valeur
   * 100 oblige à changer ces trois endroits en même temps — ce sont des
   * documents contractuels.
   */
  SOLO: 999999,
} as const;

// ============================================================================
// QUOTAS STOCKAGE
// ============================================================================

/**
 * Limites de stockage localStorage (approximatif)
 */
export const STORAGE_QUOTAS = {
  /** Utilisateurs anonymes : 50 MB */
  ANONYMOUS: 50,

  /** Utilisateurs gratuits : 500 MB */
  FREE: 500,

  /** Utilisateurs Solo : 2000 MB (2 GB) */
  SOLO: 2000,
} as const;

// ============================================================================
// PLANS & PRICING - Modèle Simplifié 3 Niveaux
// ============================================================================

export type PlanId = "ANONYMOUS" | "FREE" | "SOLO";

export const PLANS = {
  ANONYMOUS: {
    id: "ANONYMOUS",
    name: "Anonyme",
    price: 0,
    period: "lifetime",
    features: {
      aiConversations: 3, // 3 conversations IA à vie (aimant acquisition)
      polls: "unlimited", // Création manuelle illimitée
      // Illimité (2026-09-30) — on ne refuse jamais un participant. Le plafond
      // de 20 (20260923120000) est retiré par supabase/migrations/
      // 20260930120000_remove_anonymous_participant_cap.sql ; au-delà de
      // GUEST_ACCOUNT_NUDGE_RESPONSES réponses, l'organisateur sans compte est
      // invité à créer un compte (GuestOwnerPanel), sans rien bloquer.
      participants: "unlimited",
      dashboard: false,
      notifications: false,
      modifications: false,
      exportFormats: [],
      calendarIntegration: false,
      earlyAccess: false,
    },
  },
  FREE: {
    id: "FREE",
    name: "Gratuit",
    price: 0,
    period: "lifetime",
    features: {
      aiConversations: 3, // 3 conversations IA à vie, aligné anonyme (pousse vers Solo)
      polls: 10, // Plafond 10 sondages actifs
      participants: "unlimited",
      dashboard: true,
      notifications: true,
      modifications: true,
      // 15/09/2026 : le Gratuit ne garde que le CSV. JSON, Markdown et PDF
      // deviennent des formats Solo. Le CSV suffit à la portabilité RGPD :
      // personne ne perd l'accès à ses données, seulement aux formats de
      // présentation et d'interopérabilité.
      exportFormats: ["CSV"],
      calendarIntegration: false,
      earlyAccess: false,
    },
  },
  SOLO: {
    id: "SOLO",
    name: "Solo",
    price: 30, // 30€/an (2 mois offerts)
    period: "annual",
    features: {
      // Reste « unlimited » côté client DÉLIBÉRÉMENT, malgré le Fair Use de
      // 100/30 j désormais publié : la fenêtre glissante se calcule à partir de
      // `user_ai_credits.last_reset_at`, que le client ne lit pas. Afficher
      // « x / 100 » ici donnerait un compteur faux dès le premier reset. Le
      // chiffre exact vit dans la doc contractuelle et dans consume_ai_credit.
      aiConversations: "unlimited",
      polls: "unlimited",
      participants: "unlimited",
      dashboard: true,
      notifications: true,
      modifications: true,
      // 15/09/2026 : « Excel » retiré. Aucun export Excel n'existe dans le
      // dépôt — ni service, ni dépendance capable d'en produire. Le format
      // était promis dans la copie des deux produits et livré à personne.
      exportFormats: ["CSV", "PDF", "JSON", "Markdown"],
      calendarIntegration: true, // Déclaratif seulement — cf. hasCalendarIntegration()
      earlyAccess: true, // Fonctions IA en avant-première
    },
  },
} as const;

// ============================================================================
// RÉTENTION DES DONNÉES
// ============================================================================

/**
 * Durée de conservation des données avant auto-suppression.
 *
 * Depuis le 2026-07-03 (purge v2), le décompte part de la DERNIÈRE ACTIVITÉ
 * du sondage (création, modification ou dernière réponse), plus de la date
 * de création. Voir supabase/migrations/20260703091000_purge_policy_v2.sql.
 */
export const RETENTION_DAYS = {
  /** Utilisateurs anonymes : 90 jours après dernière activité */
  ANONYMOUS: 90,

  /** Utilisateurs gratuits : 90 jours après dernière activité */
  FREE: 90,

  /** Utilisateurs payants : illimité (sauf poll_retention opt-in 12/24 mois) */
  SOLO: 999999,
} as const;

// ============================================================================
// PLAFOND SONDAGES ACTIFS
// ============================================================================

/**
 * Nombre maximum de sondages actifs par plan
 *
 * Un sondage est "actif" tant qu'il n'est pas archivé/supprimé.
 * Free : 10 max — au-delà, archiver ou passer Solo.
 * Anonyme et Solo : illimités.
 */
export const MAX_ACTIVE_POLLS = {
  /** Utilisateurs anonymes : illimité (création manuelle libre) */
  ANONYMOUS: "unlimited",

  /** Utilisateurs gratuits : 10 sondages actifs maximum */
  FREE: 10,

  /** Utilisateurs Solo : illimité */
  SOLO: "unlimited",
} as const;

/**
 * Nombre maximum de modifications AI (editPollFromText) appliquées sur une
 * MÊME proposition de sondage avant que l'utilisateur soit invité à créer
 * le sondage. Sert à plafonner le coût Gemini : chaque modif consomme un
 * appel LLM. Reset à 0 à chaque nouvelle génération (generatePollFromText).
 *
 * Cf. spec : Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md
 */
export const MAX_MODIFICATIONS_PER_POLL = 3;

/**
 * Nombre maximum de messages UTILISATEUR dans une même conversation avec le
 * chat IA (questions simples comprises), tous profils confondus (anonyme,
 * gratuit, Solo). Décision de Julien du 26/10/2026.
 *
 * Distinct de MAX_MODIFICATIONS_PER_POLL (3 modifications d'une proposition) :
 * ce plafond-ci borne la longueur de la conversation, y compris les questions
 * sans modification, qui étaient illimitées.
 *
 * ⚠️ Appliqué côté CLIENT seulement (GeminiChatInterface, avant tout appel au
 * modèle). Aucun plafond équivalent n'existe dans l'edge function hyper-task :
 * il ne borne donc pas un appel direct à l'edge function. Changer la valeur
 * oblige à changer `ia-policy.md` (fr/en/es) en même temps.
 */
export const MAX_MESSAGES_PER_CONVERSATION = 20;

/**
 * Vrai si la conversation a déjà atteint le plafond de messages utilisateur :
 * le message suivant doit alors être refusé (le 20e passe, le 21e est refusé).
 */
export function isConversationMessageLimitReached(
  messages: ReadonlyArray<{ isAI?: boolean }>,
): boolean {
  const userMessages = messages.filter((m) => !m.isAI).length;
  return userMessages >= MAX_MESSAGES_PER_CONVERSATION;
}

// ============================================================================
// FORM POLLS — FAIR USE IA (invisible)
// ============================================================================

/**
 * Quotas IA spécifiques au produit Form Polls (DooQuest).
 *
 * Doctrine : cf. Docs/QUOTAS&PRICING/2026-06-07-DECISION-PRICING-FORM-POLLS.md
 * Le produit Form garde son pricing propre (Gratuit / DooQuest Pro 70 €/an) et
 * n'est PAS soumis au modèle ANONYMOUS/FREE/SOLO des Date Polls.
 *
 * Principe « Découpler le Stockage de l'Intelligence » : la collecte de
 * réponses est illimitée (stockage ≈ gratuit), seule l'IA est plafonnée.
 *
 * - FREE_LIFETIME : dégustation IA offerte pour découvrir la valeur, puis paywall.
 * - PRO_FAIR_USE_MONTHLY : plafond technique invisible (jamais affiché à
 *   l'utilisateur, présenté comme « IA illimitée* »), protège la marge contre
 *   les abus automatisés. ~7,20 €/an de coût Gemini au pire cas.
 */
export const FORM_AI_QUOTAS = {
  /** Gratuit : 5 actions IA à vie (génération + analyse), non renouvelables */
  FREE_LIFETIME: 5,

  /** Pro : Fair Use invisible de 600 requêtes IA / mois (affiché « illimité ») */
  PRO_FAIR_USE_MONTHLY: 600,
} as const;

// ============================================================================
// HELPERS - Nouveau modèle 3 niveaux
// ============================================================================

/**
 * Récupère les limites de conversations selon le plan
 */
export function getConversationLimit(planId: PlanId): number {
  return CONVERSATION_QUOTAS[planId];
}

/**
 * Récupère les limites de stockage selon le plan
 */
export function getStorageLimit(planId: PlanId): number {
  return STORAGE_QUOTAS[planId];
}

/**
 * Récupère les jours de rétention selon le plan
 */
export function getRetentionDays(planId: PlanId): number {
  return RETENTION_DAYS[planId];
}

/**
 * Récupère le plafond de sondages actifs selon le plan
 * Renvoie `null` si illimité.
 */
export function getMaxActivePolls(planId: PlanId): number | null {
  const limit = MAX_ACTIVE_POLLS[planId];
  return limit === "unlimited" ? null : limit;
}

/**
 * Vérifie si le plan a accès à l'IA
 */
export function hasAiAccess(planId: PlanId): boolean {
  const aiConversations = PLANS[planId].features.aiConversations;
  return (
    (typeof aiConversations === "number" && aiConversations > 0) ||
    aiConversations === "unlimited"
  );
}

/**
 * Vérifie si le plan a accès aux exports complets
 */
export function hasFullExports(planId: PlanId): boolean {
  const formats = PLANS[planId].features.exportFormats as readonly string[];
  // Le PDF est le seul format différenciant réellement produit par le code.
  return formats.includes("PDF");
}

/**
 * Vérifie si le plan a accès à l'intégration calendrier.
 *
 * ⚠️ 15/09/2026 — cette fonction ne verrouille rien et n'a **aucun appelant en
 * production**. Elle ne lit que la table `PLANS` ci-dessus. La réalité du code :
 * DooDates (date-polls) n'a aucune intégration calendrier, ni Google ni `.ics` ;
 * `@core/lib/google-calendar` et `@core/lib/calendar-ics` ne sont importés que
 * par `availability-polls`, où ils sont ouverts à tout le monde, payant ou non.
 *
 * L'intégration calendrier a donc cessé d'être annoncée comme une contrepartie
 * de l'abonnement Solo (décision 18) : elle a été retirée de `/pricing`, de la
 * landing et des CGV. Le drapeau `calendarIntegration` reste dans `PLANS` parce
 * que rien ne le lit, mais il ne décrit pas un verrou — ne pas le brancher sur
 * un `if` en croyant fermer quelque chose.
 */
export function hasCalendarIntegration(planId: PlanId): boolean {
  return PLANS[planId].features.calendarIntegration;
}

/**
 * Vérifie si le plan a accès à l'early access
 */
export function hasEarlyAccess(planId: PlanId): boolean {
  return PLANS[planId].features.earlyAccess;
}

/**
 * Récupère toutes les fonctionnalités d'un plan
 */
export function getPlanFeatures(planId: PlanId) {
  return PLANS[planId].features;
}

/**
 * Détermine le plan d'un utilisateur selon son état d'authentification et son abonnement
 */
export function determineUserPlan(
  isAuthenticated: boolean,
  hasSoloSubscription: boolean,
): PlanId {
  if (!isAuthenticated) return "ANONYMOUS";
  if (hasSoloSubscription) return "SOLO";
  return "FREE";
}

// ============================================================================
// PLANS PAYANTS — abstraction centrale (multi-produits)
// ============================================================================

/**
 * Valeurs de `profiles.plan_type` considérées comme un abonnement PAYANT.
 *
 * - `solo`         : abonnement suite / Date Polls (30 €/an).
 * - `dooquest_pro` : abonnement Form Polls (DooQuest Pro, 70 €/an).
 *   (`dooform_pro` = ancienne valeur live, conservée le temps de la migration
 *   des lignes existantes ; à retirer une fois la base migrée.)
 *
 * ⚠️ SOURCE DE VÉRITÉ : tout garde-fou (front ou SQL) qui distingue "payant"
 * vs "gratuit" doit s'appuyer sur cette liste (front) ou sur la fonction SQL
 * `public.is_paid_plan(text)` (DB), jamais sur un littéral `'solo'` isolé.
 * Cela garantit qu'un nouveau plan payant est reconnu partout (UI paywall,
 * rétention/purge, plafond de sondages, Fair Use IA).
 */
export const PAID_PLAN_TYPES = ["solo", "dooquest_pro", "dooform_pro"] as const;

/**
 * Famille de produit (date|form|quizz|availability) à laquelle appartient un
 * `plan_type`. Miroir EXACT de la fonction SQL `public.plan_product(text)`
 * (migration 20260708100000). Sert à raisonner « ce plan concerne quel produit »
 * côté front. `null` pour free / plan inconnu.
 *
 * ⚠️ Séparation par produit : DooDates (date, 'solo') et DooQuest (form,
 * 'dooquest_pro') sont deux produits distincts et facturés séparément. Un compte
 * peut être payant sur plusieurs produits en même temps — la source de vérité
 * est la table `product_subscriptions`, superposée sur le profil courant par
 * AuthContext selon getSupportProduct().
 */
export const PLAN_PRODUCT: Record<string, ProductFamily | undefined> = {
  solo: "date",
  pro: "date",
  starter_date: "date",
  ltd_date: "date",
  dooquest_pro: "form",
  dooform_pro: "form",
  growth_form: "form",
  ltd_form: "form",
  starter_quizz: "quizz",
  ltd_quizz: "quizz",
  premium_availability: "availability",
  ltd_availability: "availability",
};

export type ProductFamily = "date" | "form" | "quizz" | "availability";

/**
 * Famille de produit d'un plan_type (miroir SQL plan_product). null si free/inconnu.
 *
 * @public API du package `@doodates/core` : miroir TS de la fonction SQL
 * `plan_product` (migration 20260708100000). Pas encore consommé côté apps —
 * le scoping server-side par produit est en cours (cf. product_subscriptions).
 */
export function planProduct(planType?: string | null): ProductFamily | null {
  if (!planType) return null;
  return PLAN_PRODUCT[planType.toLowerCase().trim()] ?? null;
}

/** Vrai si `plan_type` correspond à un abonnement payant (insensible à la casse). */
export function isPaidPlanType(planType?: string | null): boolean {
  if (!planType) return false;
  return (PAID_PLAN_TYPES as readonly string[]).includes(
    planType.toLowerCase().trim(),
  );
}

// ============================================================================
// ESSAI SOLO — helpers d'état
// ============================================================================
//
// La vérité est en base : c'est `is_paid_plan_for_product` qui décide, et sa
// clause d'essai (migration 20260921130000) fait qu'un essai expiré n'est plus
// payant immédiatement, sans qu'aucun job n'ait tourné. Ces helpers ne servent
// donc qu'à l'AFFICHAGE — jamais à autoriser quoi que ce soit.
//
// Ils remplacent trois dérivations client incohérentes constatées sur les
// groupes. Un seul endroit, réutilisé partout.

/**
 * Forme minimale attendue. Volontairement structurelle plutôt qu'un import de
 * `Profile` : ce fichier est la source de vérité des quotas et ne doit pas
 * dépendre du contexte d'authentification.
 */
export interface TrialAwareProfile {
  plan_type?: string | null;
  trial_ends_at?: string | null;
  trial_used_at?: string | null;
  trial_campaign?: string | null;
  /** Présent = vrai abonnement LemonSqueezy, passé ou présent. */
  lemonsqueezy_subscription_id?: string | null;
  // Réponse à la PROPOSITION d'essai (migration 20260921141000), à ne pas
  // confondre avec l'essai lui-même : on peut avoir répondu « plus tard »
  // sans jamais avoir eu d'essai.
  trial_offer_declined_at?: string | null;
  trial_offer_snoozed_until?: string | null;
  trial_offer_campaign?: string | null;
}

/** Date de fin d'essai, ou `null` hors essai. */
export function trialEndsAt(profile?: TrialAwareProfile | null): Date | null {
  if (!profile?.trial_ends_at) return null;
  const d = new Date(profile.trial_ends_at);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Vrai pendant un essai en cours. Faux dès la seconde qui suit son terme. */
export function isTrialActive(profile?: TrialAwareProfile | null): boolean {
  const ends = trialEndsAt(profile);
  return ends !== null && ends.getTime() > Date.now();
}

/**
 * Accès payant effectif — **le miroir client exact de la clause d'essai** de
 * `is_paid_plan_for_product` (migration 20260921130000) :
 *
 * ```sql
 * AND (ps.lemonsqueezy_subscription_id IS NOT NULL
 *      OR ps.trial_ends_at IS NULL
 *      OR ps.trial_ends_at > now())
 * ```
 *
 * À préférer à `isPaidPlanType(profile.plan_type)` partout où l'on dispose du
 * profil. La raison tient en une phrase : **un essai non converti garde
 * `plan_type = 'solo'` indéfiniment.** Le serveur, lui, cesse immédiatement de
 * le traiter comme payant grâce à la clause ci-dessus — le retour au Gratuit
 * est paresseux, aucun processus ne constate jamais qu'un essai vient
 * d'expirer. Un client qui ne lit que `plan_type` affirme donc « Solo » à
 * quelqu'un que la base traite en Gratuit, et lui présente par exemple
 * « 30,00 € / an » sur la page Mon abonnement alors qu'il n'a rien payé.
 *
 * Ne pas compter sur le job de fin d'essai pour corriger ça : il pose bien
 * `plan_type='free'`, mais c'est un cron best-effort. La justesse de
 * l'affichage ne doit pas dépendre de son passage.
 */
export function hasPaidAccess(profile?: TrialAwareProfile | null): boolean {
  if (!isPaidPlanType(profile?.plan_type)) return false;
  // Un vrai abonnement l'emporte : la colonne d'essai peut traîner une date
  // passée sur une ligne convertie depuis (le webhook remet trial_ends_at à
  // null, mais on ne fait pas dépendre la lecture de l'écriture).
  if (profile?.lemonsqueezy_subscription_id) return true;
  if (!profile?.trial_ends_at) return true;
  return isTrialActive(profile);
}

/**
 * Plafond de conversations IA **pendant l'essai** — ni 3 (Gratuit) ni illimité
 * (Solo payant).
 *
 * Miroir client de `private.ai_seed_for` (migration 20260921132000), qui est la
 * seule occurrence serveur des chiffres 3/5/15/100/600. Les deux doivent bouger
 * ensemble ; `scripts/ci/pg-tests/trial-ai-carveout/` tient le côté serveur.
 *
 * **Pourquoi cette constante a dû exister.** `hasPaidAccess` étant vrai pendant
 * l'essai, tout le client dérivait le plan « SOLO » et donc un plafond de
 * conversations à 999999. Conséquence mesurée le 16/09 sur les définitions
 * vives : `useNotifications` déduisait « SOLO » de `limits.conversations >=
 * 99999`, et `CreditCalculationService` en tirait quatre décisions — message
 * « conversations illimitées », aucun appel à l'action, priorité « faible », et
 * surtout `shouldShowNotification` à **false**. Un essayeur n'aurait donc reçu
 * **aucun avertissement** avant de heurter le mur à la 16e conversation, après
 * s'être vu promettre l'illimité. C'est exactement le « faux essai » que la
 * relecture UX du plan cherchait à éviter, réintroduit par un autre fichier.
 */
/**
 * Durée annoncée de l'essai, en jours. Ce qui est écrit sur la page d'essai,
 * dans les CGU et dans les e-mails. La date de fin réelle est calculée côté
 * serveur sur une grille horaire fixe (`start_solo_trial`) et dépasse donc
 * toujours un peu cette durée — jamais l'inverse. Rien d'affiché ne doit
 * annoncer PLUS que ce nombre.
 */
export const TRIAL_DURATION_DAYS = 14;

export const TRIAL_AI_CONVERSATIONS = 15;

/**
 * Jours restants, arrondis vers le haut, ou `null` hors essai.
 *
 * Dérivé de la MÊME date que celle qu'on affiche : le compte à rebours et la
 * date ne peuvent donc pas se contredire, y compris la semaine du changement
 * d'heure. Ne jamais recalculer un compteur à partir d'autre chose.
 */
export function trialDaysRemaining(
  profile?: TrialAwareProfile | null,
  now: Date = new Date(),
): number | null {
  const ends = trialEndsAt(profile);
  if (ends === null || ends.getTime() <= now.getTime()) return null;
  // Plafonné à la durée ANNONCÉE. La grille horaire fixe fait finir l'essai à
  // 03 h 00 UTC, ce qui le fait durer entre 14 j 1 h et 15 j selon l'heure du
  // démarrage (jamais moins que les 14 jours promis, c'est voulu). L'arrondi
  // vers le haut affichait donc « il vous reste 15 jours » le jour même du
  // démarrage, à quelqu'un à qui la page venait de promettre 14 jours : deux
  // chiffres pour la même chose, à trente secondes d'intervalle.
  //
  // Le plafond ne mord QUE ce premier jour. Ailleurs, l'arrondi vers le haut
  // reste le bon : à 47 h de la fin, « il vous reste 2 jours » est ce que
  // n'importe qui compte — un arrondi vers le bas y répondrait « 1 jour » et
  // ferait paniquer pour rien. Le rappel J-3 côté serveur compte pareil.
  return Math.min(
    TRIAL_DURATION_DAYS,
    Math.ceil((ends.getTime() - now.getTime()) / 86_400_000),
  );
}

/**
 * Vrai si un essai a déjà été consommé — en cours ou terminé.
 *
 * Distingue « Gratuit qui n'a jamais essayé » (à qui proposer l'essai) de
 * « Gratuit dont l'essai est fini » (à qui proposer Solo directement). Sans
 * cette distinction, on repropose un essai déjà consommé, et la RPC répond
 * `TRIAL_ALREADY_USED` à quelqu'un à qui on venait de faire une promesse.
 */
export function hasUsedTrial(profile?: TrialAwareProfile | null): boolean {
  return !!profile?.trial_used_at;
}

/** Vrai pour un compte Gratuit n'ayant jamais essayé : la cible de l'offre. */
/*
 * Limite connue et assumée : ce helper ne sait pas repérer un ANCIEN abonné
 * dont la période LemonSqueezy est échue et dont `plan_type` est retombé. Le
 * client lui proposera donc l'essai, et la base refusera avec
 * `FORMER_SUBSCRIBER`. C'est le bon sens de l'erreur — le serveur tranche, le
 * client anticipe — et l'écran de démarrage traduit ce refus en une phrase
 * qui s'explique. Le faire côté client demanderait d'exposer l'historique de
 * facturation au profil, pour éviter un cas qui ne concerne aujourd'hui
 * personne.
 */
export function canStartTrial(profile?: TrialAwareProfile | null): boolean {
  return (
    !isPaidPlanType(profile?.plan_type) &&
    !hasUsedTrial(profile) &&
    !isTrialActive(profile)
  );
}

// --- Groupes ---------------------------------------------------------------
// Deux questions DISTINCTES. Les confondre produit le mur « Fonctionnalité
// Premium » sur une page qui devrait s'ouvrir avec un état vide vendeur.

/** La page groupes s'ouvre à tout compte authentifié. */
export function canViewGroups(isAuthenticated: boolean): boolean {
  return isAuthenticated;
}

/**
 * Nombre de groupes qu'un compte gratuit peut détenir et utiliser.
 *
 * Miroir de la règle serveur (`private.enforce_groups_paid_plan`) : un groupe,
 * et il doit avoir été créé HORS essai. Décision du 18/09, qui remplace le
 * « zéro groupe offert » précédent — lequel n'avait jamais été rediscuté depuis
 * le plan d'origine, qui prévoyait bien un groupe.
 */
export const FREE_USABLE_GROUPS = 1;

/**
 * Créer un groupe : plan payant, essai en cours, ou le groupe unique du
 * Gratuit s'il est encore libre.
 *
 * `hasPaidAccess` et non `isPaidPlanType` : un essai expiré garde
 * `plan_type='solo'`, et le trigger le refuserait — autant ne pas lui montrer
 * un bouton qui échouera.
 *
 * `groupesHorsEssai` compte les groupes créés HORS essai, jamais le total : un
 * groupe créé pendant l'essai ne consomme pas le droit du Gratuit, puisqu'il
 * cesse d'être utilisable à la fin. Le compter reviendrait à faire payer le
 * fait d'avoir essayé. Le paramètre est optionnel pour ne pas casser les
 * appelants qui ne connaissent pas ce nombre : sans lui, on répond comme avant.
 *
 * La clause de grand-père des comptes antérieurs vit UNIQUEMENT côté serveur
 * (trigger `date_poll_groups_enforce_plan`) — ce helper peut donc répondre
 * `false` à un compte que la base accepterait. C'est assumé : le client ne
 * fait qu'anticiper, le serveur tranche, et l'erreur va dans le sens prudent.
 */
export function canCreateGroup(
  profile?: TrialAwareProfile | null,
  groupesHorsEssai?: number,
): boolean {
  if (hasPaidAccess(profile)) return true;
  if (groupesHorsEssai === undefined) return false;
  return groupesHorsEssai < FREE_USABLE_GROUPS;
}

/**
 * Refus attendus de la RPC `start_solo_trial`.
 *
 * Ce ne sont PAS des erreurs : ce sont les réponses normales de la base quand
 * l'essai n'est pas dû. Vivent ici, dans un module sans dépendance au client
 * Supabase, pour rester testables et importables partout.
 */
export type TrialRefusal =
  | "AUTH_REQUIRED"
  | "WRONG_PRODUCT"
  | "TEST_ACCOUNT"
  | "TRIAL_ALREADY_USED"
  | "FORMER_SUBSCRIBER"
  | "ALREADY_PAID";

const TRIAL_REFUSALS: readonly TrialRefusal[] = [
  "AUTH_REQUIRED",
  "WRONG_PRODUCT",
  "TEST_ACCOUNT",
  "TRIAL_ALREADY_USED",
  "FORMER_SUBSCRIBER",
  "ALREADY_PAID",
];

/**
 * Extrait le code de refus d'un message PostgREST, ou `null` si c'est une
 * vraie panne.
 *
 * La distinction qui compte : un refus s'explique à l'utilisateur, une panne
 * se logue. Les mélanger affiche « une erreur est survenue » à quelqu'un dont
 * l'essai est simplement déjà consommé.
 */
export function parseTrialRefusal(
  message?: string | null,
): TrialRefusal | null {
  if (!message) return null;
  return TRIAL_REFUSALS.find((code) => message.includes(code)) ?? null;
}

// --- Exports ------------------------------------------------------------

/**
 * Formats d'export réservés aux plans payants (essai inclus).
 *
 * Le CSV reste gratuit et n'apparaît volontairement pas ici : c'est lui qui
 * porte la portabilité des données. Verrouiller les autres formats ne prive
 * donc personne de ses données, seulement de la mise en forme.
 */
export const PAID_EXPORT_FORMATS = ["JSON", "Markdown", "PDF"] as const;

export type ExportFormat = "CSV" | (typeof PAID_EXPORT_FORMATS)[number];

/**
 * Vrai si ce profil peut exporter dans ce format.
 *
 * Comme pour les groupes, ce helper n'AUTORISE rien : il n'y a aujourd'hui
 * aucun verrou serveur sur l'export (le fichier est produit côté client). Il
 * décide de l'affichage, et c'est tout ce qu'il prétend faire.
 */
export function canExportFormat(
  profile: TrialAwareProfile | null | undefined,
  format: ExportFormat,
): boolean {
  if (format === "CSV") return true;
  // hasPaidAccess, pas isPaidPlanType : un essai expiré garde plan_type=solo.
  return hasPaidAccess(profile);
}

// ---------------------------------------------------------------------------
// Répondre à la proposition d'essai — non merci, ou plus tard
// ---------------------------------------------------------------------------

/**
 * Refus attendus de la RPC `respond_to_trial_offer`.
 *
 * Même distinction que pour `start_solo_trial` : un refus s'explique, une
 * panne se logue. Les confondre afficherait « une erreur est survenue » à
 * quelqu'un qui n'a simplement plus d'offre à reporter.
 */
export type TrialOfferRefusal =
  | "AUTH_REQUIRED"
  | "WRONG_PRODUCT"
  | "INVALID_RESPONSE"
  | "INVALID_SNOOZE"
  | "NOTHING_TO_DEFER";

const TRIAL_OFFER_REFUSALS: readonly TrialOfferRefusal[] = [
  "AUTH_REQUIRED",
  "WRONG_PRODUCT",
  "INVALID_RESPONSE",
  "INVALID_SNOOZE",
  "NOTHING_TO_DEFER",
];

export function parseTrialOfferRefusal(
  message?: string | null,
): TrialOfferRefusal | null {
  if (!message) return null;
  return TRIAL_OFFER_REFUSALS.find((code) => message.includes(code)) ?? null;
}

/**
 * Les trois durées proposées derrière « Plus tard ».
 *
 * POURQUOI TROIS CHOIX FIXES ET NON UN SÉLECTEUR DE DATE. La question posée
 * n'est pas « quand veux-tu qu'on te le repropose ? » — personne ne sait
 * prédire son intérêt pour un produit — mais « tu organises quelque chose
 * bientôt ? », à laquelle on répond depuis son propre calendrier. Un
 * sélecteur invite une date si lointaine qu'elle est un refus déguisé, et il
 * ajoute une surface de format et de fuseau horaire pour un gain nul.
 *
 * Les libellés restent APPROXIMATIFS à dessein (« un mois environ ») : la
 * personne estime, elle ne prend pas rendez-vous, et un libellé précis
 * promettrait une exactitude que ni elle ni nous ne pouvons tenir.
 */
export type TrialSnoozeChoice = "few_days" | "one_month" | "few_months";

export const TRIAL_SNOOZE_CHOICES: readonly {
  id: TrialSnoozeChoice;
  days: number;
}[] = [
  { id: "few_days", days: 7 },
  { id: "one_month", days: 30 },
  { id: "few_months", days: 75 },
];

/**
 * Bornes du report, en jours. DOIVENT rester d'accord avec
 * `private.trial_snooze_bounds()` (migration 20260921141000) : la RPC refuse
 * hors bornes avec INVALID_SNOOZE, et un client qui proposerait une durée
 * refusée offrirait un bouton qui ne marche pas.
 */
export const TRIAL_SNOOZE_MIN_DAYS = 1;
export const TRIAL_SNOOZE_MAX_DAYS = 180;

/** Échéance ISO d'un choix, calculée côté client puis validée côté serveur. */
export function trialSnoozeUntil(choice: TrialSnoozeChoice): string {
  const days =
    TRIAL_SNOOZE_CHOICES.find((c) => c.id === choice)?.days ??
    TRIAL_SNOOZE_CHOICES[0].days;
  return new Date(Date.now() + days * 86_400_000).toISOString();
}

/**
 * La proposition doit-elle être affichée ?
 *
 * Vrai si le compte peut démarrer un essai ET n'a pas de réponse en cours :
 * ni « non merci », ni un report dont l'échéance n'est pas arrivée.
 *
 * LIMITE CONNUE, ET ELLE N'EST PAS NOUVELLE. La campagne
 * (`private.trial_current_campaign()`) est une notion SERVEUR que le client ne
 * voit pas. Après un changement de campagne, le serveur rouvre le droit de
 * répondre alors que ce calcul continue de masquer le bandeau pour qui avait
 * refusé. C'est exactement la limite que porte déjà `hasUsedTrial`, qui lit
 * `trial_used_at` sans regarder la campagne non plus : le serveur décide, le
 * client affiche. La corriger demanderait d'exposer la campagne courante, ce
 * qui est un chantier à part — et non un second mécanisme improvisé ici.
 */
export function trialOfferPending(profile?: TrialAwareProfile | null): boolean {
  if (!canStartTrial(profile)) return false;
  if (profile?.trial_offer_declined_at) return false;
  const snoozed = profile?.trial_offer_snoozed_until;
  if (snoozed) {
    const until = new Date(snoozed);
    if (!Number.isNaN(until.getTime()) && until.getTime() > Date.now()) {
      return false;
    }
  }
  return true;
}

/** Report arrivé à échéance : le bandeau revient, avec un AUTRE texte. */
export function trialSnoozeElapsed(
  profile?: TrialAwareProfile | null,
): boolean {
  const snoozed = profile?.trial_offer_snoozed_until;
  if (!snoozed || profile?.trial_offer_declined_at) return false;
  const until = new Date(snoozed);
  if (Number.isNaN(until.getTime())) return false;
  return until.getTime() <= Date.now() && canStartTrial(profile);
}

/**
 * Seuil de réponses au-delà duquel l'organisateur SANS COMPTE d'un sondage
 * est invité à créer un compte gratuit (GuestOwnerPanel, page de résultats).
 * Invitation uniquement : aucun vote n'est refusé, aucune réponse masquée.
 * Remplace le plafond dur de 20 participants retiré le 2026-09-30.
 */
export const GUEST_ACCOUNT_NUDGE_RESPONSES = 20;
